import { memo, useCallback, useMemo, useState, type ChangeEvent } from 'react';
import { Box, Button, FormControl, IconButton, MenuItem, Select, TextField, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { QueryField, QueryRule } from '../../../../../types/QueryBuilder';
import { useLanguage } from '../../../../../context/LanguageContext';
import { useBrandConfig } from '../../../../../context/BrandConfigContext';
import ValueEditor from '../ValueEditors';
import CheckboxEditor from '../ValueEditors/CheckboxEditor';
import SelectionDialogEditor from '../ValueEditors/SelectionDialogEditor';
import DualListSelectionDialogEditor from '../ValueEditors/DualListSelectionDialogEditor';
import StoreListImport from '../StoreListImport';
import PurchasePeriodQuickSelect, { lastMonthsRange, parseMonthsFromRange } from '../PurchasePeriodQuickSelect';
import {
    COUNTER_FIELD_NAME,
    DIALOG_SELECT_FIELD_NAMES,
    DUAL_LIST_FIELD_NAMES,
    DUAL_LIST_NO_SEARCH_FIELD_NAMES,
    FIELD_GRID_POSITION,
    IDENTIFIER_LIST_FIELD_NAME,
    IDENTIFIERS_VISIBLE_LIST_CONFIG_KEY,
    parseConfigIdList,
    PURCHASE_PERIOD_FIELD_NAME,
    ROW_SPAN_2_FIELD_NAMES,
    STORE_FIELD_NAME,
    resolveFieldLabel,
    resolveFieldValues,
} from './fieldConfig';

interface FieldRowProps {
    field: QueryField;
    rule: QueryRule | undefined;
    onUpdateField: (fieldName: string, data: Partial<QueryRule>) => void;
    pairedField?: QueryField;
    pairedRule?: QueryRule;
    pairedIsOptinStatus: boolean;
}

const rowSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 1,
    p: 1.5,
    bgcolor: '#ffffff',
    borderRadius: 1,
    border: '1px solid #e0e0e0',
    '&:hover': { borderColor: '#556ee6', boxShadow: '0 2px 8px rgba(85, 110, 230, 0.1)' },
};

function FieldLabel({ field }: { field: QueryField }) {
    const { t } = useLanguage();
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#495057' }}>
                {resolveFieldLabel(field, t)}
            </Typography>
            {field.name === COUNTER_FIELD_NAME && (
                <Tooltip title={t('pages.clients.queryBuilder.simple.customerAccount.tooltip')} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                </Tooltip>
            )}
        </Box>
    );
}

function OptinStatusRow({ field, rule, pairedField, pairedRule, onUpdateField }: {
    field: QueryField;
    rule: QueryRule | undefined;
    pairedField: QueryField;
    pairedRule: QueryRule | undefined;
    onUpdateField: (fieldName: string, data: Partial<QueryRule>) => void;
}) {
    const { t } = useLanguage();
    const handleValueChange = useCallback(
        (value: QueryRule['value']) => onUpdateField(field.name, { value }),
        [field.name, onUpdateField]
    );
    const handlePairedValueChange = useCallback(
        (value: QueryRule['value']) => onUpdateField(pairedField.name, { value }),
        [pairedField.name, onUpdateField]
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FieldLabel field={field} />
                <ValueEditor
                    ruleId={rule?.id || field.name}
                    field={field}
                    operator={rule?.operator || field.defaultOperator || '='}
                    value={rule?.value}
                    onChange={handleValueChange}
                    compact
                />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1.5, borderTop: '1px solid #e0e0e0' }}>
                <FieldLabel field={pairedField} />
                <CheckboxEditor
                    ruleId={pairedRule?.id || pairedField.name}
                    value={pairedRule?.value ?? null}
                    values={resolveFieldValues(pairedField, t) || []}
                    onChange={handlePairedValueChange}
                    compact
                />
            </Box>
        </Box>
    );
}

// "Critères de comportement d'achat": a simple Actifs/Inactifs select + months field drives the
// hidden TRANSACTION_NB (>=1 purchase vs. exactly 0) and TRANSACTION_PERIOD rules. Nothing is added
// to the targeting until the user explicitly clicks "Sélectionner" — until then both rules stay
// empty so they're dropped by isRuleValid. Once applied, the inputs lock and a clear button (✕)
// resets both rules so the criterion can be edited again.
function PurchaseActivityRow({ field, rule, pairedField, pairedRule, onUpdateField }: {
    field: QueryField;
    rule: QueryRule | undefined;
    pairedField: QueryField;
    pairedRule: QueryRule | undefined;
    onUpdateField: (fieldName: string, data: Partial<QueryRule>) => void;
}) {
    const { t } = useLanguage();

    const isApplied = rule?.operator === 'between' && typeof rule?.value === 'string' && rule.value.includes('|');
    const isInactive = pairedRule?.operator === '=' && Number(pairedRule?.value) === 0;

    const [activity, setActivity] = useState<'active' | 'inactive'>(isInactive ? 'inactive' : 'active');
    const [months, setMonths] = useState<number>(() => parseMonthsFromRange(rule?.value) ?? 12);

    const handleMonthsChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const parsed = parseInt(event.target.value, 10);
        setMonths(Number.isNaN(parsed) ? 1 : Math.max(1, parsed));
    }, []);

    const handleApply = useCallback(() => {
        onUpdateField(field.name, { operator: 'between', value: lastMonthsRange(months) });
        onUpdateField(pairedField.name, activity === 'inactive' ? { operator: '=', value: 0 } : { operator: '>=', value: 1 });
    }, [field.name, pairedField.name, months, activity, onUpdateField]);

    const handleClear = useCallback(() => {
        onUpdateField(field.name, { operator: undefined, value: undefined });
        onUpdateField(pairedField.name, { operator: undefined, value: undefined });
    }, [field.name, pairedField.name, onUpdateField]);

    return (
        <>
            <FieldLabel field={field} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%' }}>
                <Typography variant="caption" sx={{ color: '#495057' }}>
                    {t('pages.clients.queryBuilder.simple.quickPeriod.prefix')}
                </Typography>
                <FormControl size="small" sx={{ minWidth: 130, flexShrink: 0 }}>
                    <Select
                        value={activity}
                        disabled={isApplied}
                        onChange={(e) => setActivity(e.target.value as 'active' | 'inactive')}
                    >
                        <MenuItem value="active">{t('pages.clients.queryBuilder.simple.purchaseActivity.active')}</MenuItem>
                        <MenuItem value="inactive">{t('pages.clients.queryBuilder.simple.purchaseActivity.inactive')}</MenuItem>
                    </Select>
                </FormControl>
                <TextField
                    type="number"
                    size="small"
                    value={months}
                    onChange={handleMonthsChange}
                    disabled={isApplied}
                    inputProps={{ min: 1 }}
                    sx={{ width: 72 }}
                />
                <Typography variant="caption" sx={{ color: '#495057' }}>
                    {t('pages.clients.queryBuilder.simple.quickPeriod.suffix')}
                </Typography>
                {isApplied ? (
                    <Tooltip title={t('pages.clients.queryBuilder.simple.quickPeriod.clearTooltip')} arrow>
                        <IconButton size="small" onClick={handleClear} sx={{ border: '1px solid', borderColor: 'divider' }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <Button size="small" variant="outlined" onClick={handleApply}>
                        {t('pages.clients.queryBuilder.simple.quickPeriod.apply')}
                    </Button>
                )}
            </Box>
        </>
    );
}

function DualListRow({ field, rule, onUpdateField }: {
    field: QueryField;
    rule: QueryRule | undefined;
    onUpdateField: (fieldName: string, data: Partial<QueryRule>) => void;
}) {
    const { t } = useLanguage();
    const { getConfigValue } = useBrandConfig();
    const handleChange = useCallback(
        (data: { value: string[]; notValue: string[] }) => onUpdateField(field.name, { operator: 'in', ...data }),
        [field.name, onUpdateField]
    );

    const hideSearch = DUAL_LIST_NO_SEARCH_FIELD_NAMES.has(field.name);
    const outerLabels = hideSearch
        ? {
            include: t('pages.clients.queryBuilder.simple.dualSelectionDialog.belongsToTypes'),
            exclude: t('pages.clients.queryBuilder.simple.dualSelectionDialog.doesNotBelongToTypes'),
        }
        : undefined;

    // Per-enseigne config restricts which identifier types are selectable at all (the rest aren't
    // offered any more, matching the previous BeOne UI's `query_identifiers` list) — when no config
    // exists for this enseigne, every value from the API stays visible, untouched.
    const values = useMemo(() => {
        const resolved = resolveFieldValues(field, t) || [];
        if (field.name !== IDENTIFIER_LIST_FIELD_NAME) return resolved;
        const visibleIds = parseConfigIdList(getConfigValue(IDENTIFIERS_VISIBLE_LIST_CONFIG_KEY));
        if (!visibleIds?.length) return resolved;
        return resolved.filter(v => visibleIds.includes(v.name));
    }, [field, t, getConfigValue]);

    return (
        <>
            <FieldLabel field={field} />
            <DualListSelectionDialogEditor
                ruleId={rule?.id || field.name}
                includeValues={rule && Array.isArray(rule.value) ? rule.value.map(String) : []}
                excludeValues={rule?.notValue ?? []}
                values={values}
                title={resolveFieldLabel(field, t)}
                onChange={handleChange}
                hideSearch={hideSearch}
                outerLabels={outerLabels}
                dialogMaxWidth={hideSearch ? 'sm' : 'md'}
            />
        </>
    );
}

function DefaultRow({ field, rule, pairedField, pairedRule, onUpdateField }: {
    field: QueryField;
    rule: QueryRule | undefined;
    pairedField: QueryField | undefined;
    pairedRule: QueryRule | undefined;
    onUpdateField: (fieldName: string, data: Partial<QueryRule>) => void;
}) {
    const { t } = useLanguage();

    const handleValueChange = useCallback(
        (value: QueryRule['value']) => onUpdateField(field.name, { value }),
        [field.name, onUpdateField]
    );
    const handlePairedValueChange = useCallback(
        (value: QueryRule['value']) => pairedField && onUpdateField(pairedField.name, { value }),
        [pairedField, onUpdateField]
    );
    const handleApplyPeriod = useCallback(
        (value: string) => onUpdateField(field.name, { operator: 'between', value }),
        [field.name, onUpdateField]
    );

    return (
        <>
            <FieldLabel field={field} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, flexGrow: 1 }}>
                <Box sx={pairedField ? { flex: '0 1 260px', minWidth: '220px' } : { flexGrow: 1, minWidth: '200px', alignSelf: 'stretch' }}>
                    {DIALOG_SELECT_FIELD_NAMES.has(field.name) ? (
                        <SelectionDialogEditor
                            ruleId={rule?.id || field.name}
                            value={rule?.value}
                            values={resolveFieldValues(field, t) || []}
                            title={resolveFieldLabel(field, t)}
                            onChange={handleValueChange}
                        />
                    ) : (
                        <ValueEditor
                            ruleId={rule?.id || field.name}
                            field={field.values ? { ...field, values: resolveFieldValues(field, t) } : field}
                            operator={rule?.operator || field.defaultOperator || '='}
                            value={rule?.value}
                            onChange={handleValueChange}
                            compact
                            betweenSwapIfInverted={field.name === COUNTER_FIELD_NAME}
                        />
                    )}
                </Box>
                {pairedField && (
                    (pairedField.values?.length ?? 0) > 1 ? (
                        <FormControl size="small" sx={{ minWidth: 100, flexShrink: 0 }}>
                            <Select
                                value={typeof pairedRule?.value === 'string' ? pairedRule.value : ''}
                                onChange={(e) => handlePairedValueChange(e.target.value)}
                            >
                                {pairedField.values!.map(v => (
                                    <MenuItem key={v.name} value={v.name}>{v.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : (pairedField.values?.length ?? 0) === 1 ? (
                        <Typography variant="body2" sx={{ color: '#495057' }}>
                            {pairedField.values![0].label}
                        </Typography>
                    ) : null
                )}
                {field.name === STORE_FIELD_NAME && (
                    <StoreListImport values={field.values || []} selectedValue={rule?.value} onChange={handleValueChange} />
                )}
                {field.name === PURCHASE_PERIOD_FIELD_NAME && (
                    <PurchasePeriodQuickSelect onApply={handleApplyPeriod} />
                )}
            </Box>
        </>
    );
}

const FieldRow = memo(function FieldRow({ field, rule, onUpdateField, pairedField, pairedRule, pairedIsOptinStatus }: FieldRowProps) {
    const isRowSpan2 = ROW_SPAN_2_FIELD_NAMES.has(field.name);
    const gridPosition = FIELD_GRID_POSITION[field.name];

    return (
        <Box
            sx={{
                ...rowSx,
                gridColumn: gridPosition ? { md: gridPosition.column } : undefined,
                gridRow: gridPosition
                    ? { md: isRowSpan2 ? `${gridPosition.row} / span 2` : `${gridPosition.row}` }
                    : (isRowSpan2 ? 'span 2' : undefined),
                alignSelf: isRowSpan2 ? 'stretch' : undefined,
            }}
        >
            {pairedField && pairedIsOptinStatus ? (
                <OptinStatusRow field={field} rule={rule} pairedField={pairedField} pairedRule={pairedRule} onUpdateField={onUpdateField} />
            ) : field.name === PURCHASE_PERIOD_FIELD_NAME && pairedField ? (
                <PurchaseActivityRow field={field} rule={rule} pairedField={pairedField} pairedRule={pairedRule} onUpdateField={onUpdateField} />
            ) : DUAL_LIST_FIELD_NAMES.has(field.name) ? (
                <DualListRow field={field} rule={rule} onUpdateField={onUpdateField} />
            ) : (
                <DefaultRow field={field} rule={rule} pairedField={pairedField} pairedRule={pairedRule} onUpdateField={onUpdateField} />
            )}
        </Box>
    );
});

export default FieldRow;
